import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Phone, X } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const KEY = "bl_phone_nudge_until";
const snoozed = () => Number(localStorage.getItem(KEY) || 0) > Date.now();
export const openMyCard = () => window.dispatchEvent(new CustomEvent("bl:open-card"));

// Nudges you to put a shared phone number on your card, so friends' phone books can find you
export const PhoneNudge = ({ testId, inSettings = false }) => {
  const qc = useQueryClient();
  const { data: card } = useQuery({ queryKey: ["card"], queryFn: api.card });
  const [phone, setPhone] = useState("");
  const [hidden, setHidden] = useState(snoozed);
  if (!card) return null;
  const has = card.values.phone && card.share.phone;
  if (has && !inSettings) return null;
  if (!has && hidden && !inSettings) return null;
  const later = () => { localStorage.setItem(KEY, String(Date.now() + 30 * 864e5)); setHidden(true); };
  const save = async (e) => {
    e.preventDefault();
    if (phone.replace(/\D/g, "").length < 7) return toast.error("Enter your full phone number");
    try {
      qc.setQueryData(["card"], await api.saveCard({ ...card.values, phone: phone.trim(), share: { ...card.share, phone: true } }));
      toast.success("Your number is on your card — friends can now find you from their phone book");
    } catch { toast.error("Couldn't save your number"); }
  };
  if (has) {
    return (
      <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex items-center gap-3" data-testid={testId}>
        <Phone size={16} className="text-emerald-300 shrink-0" />
        <p className="flex-1 text-sm text-slate-300">Friends can find you by <span className="text-slate-100" data-testid={`${testId}-number`}>{card.values.phone}</span></p>
        <button onClick={openMyCard} className="text-xs text-emerald-300 hover:text-emerald-200" data-testid={`${testId}-open-card`}>Edit card</button>
      </section>
    );
  }
  return (
    <section className="rounded-xl border border-emerald-400/25 bg-emerald-400/[0.06] p-4 space-y-3" data-testid={testId}>
      <div className="flex items-start gap-3">
        <Phone size={16} className="text-emerald-300 mt-0.5 shrink-0" />
        <div className="flex-1 text-sm">
          <p className="font-medium text-slate-100">Add your phone number to your card</p>
          <p className="text-xs text-slate-400 mt-0.5">{card.values.phone ? "Your number is saved but not shared, so " : "Then "}friends who import their phone book will see you're on the bucketlist.</p>
        </div>
        {!inSettings && <button onClick={later} className="p-1 text-slate-500 hover:text-slate-200" aria-label="Not now" data-testid={`${testId}-dismiss`}><X size={14} /></button>}
      </div>
      {card.values.phone ? (
        <Button size="sm" onClick={() => api.saveCard({ ...card.values, share: { ...card.share, phone: true } }).then((c) => { qc.setQueryData(["card"], c); toast.success("Your number is now shared on your card"); })} className="bg-emerald-400 text-[#04120c] hover:bg-emerald-300" data-testid={`${testId}-share`}>Share {card.values.phone}</Button>
      ) : (
        <form onSubmit={save} className="flex gap-2">
          <Input type="tel" inputMode="tel" placeholder="+354 555 1234" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-9 flex-1" data-testid={`${testId}-input`} />
          <Button type="submit" size="sm" className="bg-emerald-400 text-[#04120c] hover:bg-emerald-300" data-testid={`${testId}-save`}>Save</Button>
        </form>
      )}
      <div className="flex items-center gap-4 text-xs">
        <button onClick={openMyCard} className="text-emerald-300 hover:text-emerald-200" data-testid={`${testId}-open-card`}>Open my contact card</button>
        {!inSettings && <button onClick={later} className="text-slate-400 hover:text-slate-200" data-testid={`${testId}-later`}>Not now</button>}
      </div>
    </section>
  );
};
